import React, { useState } from 'react';
import { useQuery } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import { ManagedReportItemInputMap } from '../Shared/ManagedReport';
import {
    ManagedFormRule,
    ManagedFormErrorInfo,
    ManagedApiError,
    ManagedAdminEditorMode,
    ManagedAdminActionMode,
    ManagedAdminChildOperation,
    ManagedActionModal,
    ManagedActionModalState,
    buildManagedActionModalState,
    SlimDataSetInputAdminModel,
    SlimReportInputAdminModel,
    mapSlimDataSetInputAdminModel,
    mapSlimReportInputAdminModel,
} from '../Shared/ManagedHelpers';
import {
    AddIcon,
    Button,
    Col,
    DeleteIcon,
    Divider,
    EditIcon,
    Form,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import GetReportInputAdminModelsRequest from '../../Interfaces/Requests/Administration/GetReportInputAdminModelsRequest';
import GetDataSetInputAdminModelsRequest from '../../Interfaces/Requests/Administration/GetDataSetInputAdminModelsRequest';
import GetReportInputAdminModelsResponse from '../../Interfaces/Responses/Administration/GetReportInputAdminModelResponse';
import GetDataSetInputAdminModelsResponse from '../../Interfaces/Responses/Administration/GetDataSetInputAdminModelResponse';
import Administration from '../../Constants/Endpoints/Administration';

export type ManagedReportItemInputMapEditResult = {
    operation: ManagedAdminChildOperation;
    reportItemInputMap: ManagedReportItemInputMap;
};

type AUDReportItemInputMapFormValues = {
    riimReportInputID: number;
    riimDataSetInputID: number;
};

type AUDReportItemInputMapsProps = {
    reportID: number;
    reportName: string;
    reportGroupName: string;
    reportPageID: number;
    reportPageTitle: string;
    reportItemID: number;
    reportItemTitle: string;
    reportItemDataSetID: number;
    reportItemInputMaps: ManagedReportItemInputMap[];
    initialSelectedReportItemInputMapID?: number;
    initialMode?: ManagedAdminActionMode;
    handleSetReportItemInputMaps: (
        result: ManagedReportItemInputMapEditResult
    ) => void;
    close: () => void;
};

const AUDReportItemInputMaps: React.FC<AUDReportItemInputMapsProps> = ({ close, ...props }) => {
    const [formAUDReportItemInputMap] = Form.useForm();

    const reportItemInputMaps = props.reportItemInputMaps ?? [];

    const getInitialRIIMMode = (): ManagedAdminActionMode => {
        switch (props.initialMode) {
            case 'update':
                return 'update';
            case 'delete':
                return 'delete';
            case 'add':
            default:
                return 'add';
        }
    };

    const getInitialSelectedReportItemInputMap = (): ManagedReportItemInputMap | undefined => {
        if (!props.initialSelectedReportItemInputMapID || props.initialSelectedReportItemInputMapID <= 0) {
            return undefined;
        }

        return reportItemInputMaps.find(
            (reportItemInputMap) => reportItemInputMap.id === props.initialSelectedReportItemInputMapID
        );
    };

    const buildReportItemInputMapNameConstruct = (riim: ManagedReportItemInputMap | undefined): string => {
        if (!riim) {
            return '';
        }

        const formatReportItemInputMapNameID = (id?: number | null): string => {
            return id && id > 0 ? String(id) : 'new';
        };

        return 'RInID: [' + formatReportItemInputMapNameID(riim.reportInputID) +
            '] / DSInID: [' + formatReportItemInputMapNameID(riim.dataSetInputID) +
            '] / RItID: [' + formatReportItemInputMapNameID(riim.reportItemID) + ']';
    };

    const initialSelectedReportItemInputMap = getInitialSelectedReportItemInputMap();
    const initialRIIMMode = getInitialRIIMMode();

    const initialRIIMFormValues = initialRIIMMode === 'add'
        ? {
            riimReportInputID: 0,
            riimDataSetInputID: 0,
        }
        : {
            riimReportInputID: initialSelectedReportItemInputMap?.reportInputID ?? 0,
            riimDataSetInputID: initialSelectedReportItemInputMap?.dataSetInputID ?? 0,
        };

    const [addButtonClicked, setAddButtonClicked] = useState(initialRIIMMode === 'add');
    const [updButtonClicked, setUpdButtonClicked] = useState(initialRIIMMode === 'update');
    const [delButtonClicked, setDelButtonClicked] = useState(initialRIIMMode === 'delete');

    const riimIncludeDisabled = false;

    const formatRIIMDisplayID = (id?: number | null): string => {
        return id && id > 0 ? String(id) : 'new';
    };

    const riimPropsReportID = props.reportID > 0 ? props.reportID : 0;
    const riimPropsReportName = props.reportName !== '' ? props.reportName : 'New Report';
    const riimPropsReportGroupName = props.reportGroupName !== '' ? props.reportGroupName : 'New Report Group';
    const riimPropsReportPageID = props.reportPageID > 0 ? props.reportPageID : 0;
    const riimPropsReportPageTitle = props.reportPageTitle !== '' ? props.reportPageTitle : 'New Report Page';
    const riimPropsReportItemID = props.reportItemID > 0 ? props.reportItemID : 0;
    const riimPropsReportItemTitle = props.reportItemTitle !== '' ? props.reportItemTitle : 'New Report Item';
    const riimPropsReportItemDataSetID = props.reportItemDataSetID > 0 ? props.reportItemDataSetID : 0;

    const [riimSelectedReportItemInputMapID, setRIIMSelectedReportItemInputMapID] = useState(initialSelectedReportItemInputMap?.id ?? -1);
    const [riimSelectedReportItemInputMapTitle, setRIIMSelectedReportItemInputMapTitle] = useState(
        buildReportItemInputMapNameConstruct(initialSelectedReportItemInputMap)
    );

    const [riimReportInputID, setRIIMReportInputID] = useState<number>(initialSelectedReportItemInputMap?.reportInputID ?? 0);
    const [riimDataSetInputID, setRIIMDataSetInputID] = useState<number>(initialSelectedReportItemInputMap?.dataSetInputID ?? 0);

    const [managedActionModalState, setManagedActionModalState] = useState<ManagedActionModalState>({
        open: false,
        mode: 'info',
        title: '',
        message: '',
        isError: false,
    });

    const validateRIIMDetailsData = (rule: ManagedFormRule, value: string) => {
        switch (rule.field) {
            case 'riimReportInputID': {
                const newRIIMReportInputID = formAUDReportItemInputMap.getFieldValue('riimReportInputID');

                return (!newRIIMReportInputID || newRIIMReportInputID === 0)
                    ? Promise.reject()
                    : Promise.resolve();
            }

            case 'riimDataSetInputID': {
                const newRIIMDataSetInputID = formAUDReportItemInputMap.getFieldValue('riimDataSetInputID');

                return (!newRIIMDataSetInputID || newRIIMDataSetInputID === 0)
                    ? Promise.reject()
                    : Promise.resolve();
            }

            default:
                return Promise.reject();
        }
    };

    const handleRIIMLoadError = (title: string, err: unknown) => {
        console.error(title + ': ', err);

        const apiError = err as ManagedApiError;

        const message = apiError?.response?.data?.message
            ? `${title}: ${apiError.response.data.message}`
            : `${title}: ${String(err)}`;

        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'Report Item Input Map Load Error',
                message,
                true
            )
        );
    };

    const { data: reportInputs = [], isLoading: isGetReportInputsLoading } = useQuery<GetReportInputAdminModelsResponse[], Error, SlimReportInputAdminModel[]>(
        ['getReportInputAdminModelsRequest', riimPropsReportID, riimIncludeDisabled],
        async (): Promise<GetReportInputAdminModelsResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_REPORT_INPUT_ADMIN_MODELS}`;
                const requestData: GetReportInputAdminModelsRequest = {
                    gri_reportID: riimPropsReportID,
                    gri_includeDisabled: riimIncludeDisabled,
                };

                return await AxiosUtil.get<GetReportInputAdminModelsResponse[], GetReportInputAdminModelsRequest>(url, requestData);
            } catch (err) {
                handleRIIMLoadError('GET_REPORT_INPUT_ADMIN_MODELS: [Get Report Input Admin Models AUD] Internal Error', err);
                return Promise.reject(err);
            }
        },
        {
            enabled: riimPropsReportID > 0,
            onError: (err: Error) => {
                handleRIIMLoadError('GET_REPORT_INPUT_ADMIN_MODELS: [Get Report Input Admin Models AUD] Internal Error', err);
            },
            select: (response: GetReportInputAdminModelsResponse[]): SlimReportInputAdminModel[] => {
                return response
                    .map(mapSlimReportInputAdminModel)
                    .filter((eachReportInput) => !eachReportInput.deleted)
                    .sort((a, b) => a.title.localeCompare(b.title));
            },
        }
    );

    const { data: dataSetInputs = [], isLoading: isGetDataSetInputsLoading } = useQuery<GetDataSetInputAdminModelsResponse[], Error, SlimDataSetInputAdminModel[]>(
        ['getDataSetInputAdminModelsRequest', riimPropsReportItemDataSetID, riimIncludeDisabled],
        async (): Promise<GetDataSetInputAdminModelsResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_DATASET_INPUT_ADMIN_MODELS}`;
                const requestData: GetDataSetInputAdminModelsRequest = {
                    gdsi_datasetID: riimPropsReportItemDataSetID,
                    gdsi_includeDisabled: riimIncludeDisabled,
                };

                return await AxiosUtil.get<GetDataSetInputAdminModelsResponse[], GetDataSetInputAdminModelsRequest>(url, requestData);
            } catch (err) {
                handleRIIMLoadError('GET_DATASET_INPUT_ADMIN_MODELS: [Get DataSet Input Admin Models AUD] Internal Error', err);
                return Promise.reject(err);
            }
        },
        {
            enabled: riimPropsReportItemDataSetID > 0,
            onError: (err: Error) => {
                handleRIIMLoadError('GET_DATASET_INPUT_ADMIN_MODELS: [Get DataSet Input Admin Models AUD] Internal Error', err);
            },
            select: (response: GetDataSetInputAdminModelsResponse[]): SlimDataSetInputAdminModel[] => {
                return response
                    .map(mapSlimDataSetInputAdminModel)
                    .sort((a, b) => a.title.localeCompare(b.title));
            },
        }
    );

    const handleRIIMReportInputIDChange = (value: number) => {
        setRIIMReportInputID(value);
        formAUDReportItemInputMap.setFieldValue('riimReportInputID', value);
    };

    const handleRIIMDataSetInputIDChange = (value: number) => {
        setRIIMDataSetInputID(value);
        formAUDReportItemInputMap.setFieldValue('riimDataSetInputID', value);
    };

    const onFinish = (values: AUDReportItemInputMapFormValues) => {
        const newManagedReportItemInputMap: ManagedReportItemInputMap = {
            id: addButtonClicked ? 0 : (updButtonClicked ? riimSelectedReportItemInputMapID : (delButtonClicked ? riimSelectedReportItemInputMapID : 0)),
            reportInputID: values.riimReportInputID,
            dataSetInputID: values.riimDataSetInputID,
            reportItemID: riimPropsReportItemID,
        };

        props.handleSetReportItemInputMaps({
            operation: getRIIMOperation(),
            reportItemInputMap: newManagedReportItemInputMap,
        });

        close();
    };

    const setRIIMMode = (mode: ManagedAdminActionMode) => {
        setAddButtonClicked(mode === 'add');
        setUpdButtonClicked(mode === 'update');
        setDelButtonClicked(mode === 'delete');
    };

    const clearRIIMSelection = () => {
        setRIIMSelectedReportItemInputMapID(-1);
        setRIIMSelectedReportItemInputMapTitle('');

        setRIIMMode('add');
        handleRIIMReportItemInputMapResetForm('blank');
    };

    const handleRIIMSelectionChange = (e: number) => {
        const selectedReportItemInputMap = reportItemInputMaps.find(
            (reportItemInputMap) => reportItemInputMap.id === e
        );

        setRIIMSelectedReportItemInputMapID(e);
        setRIIMSelectedReportItemInputMapTitle(
            buildReportItemInputMapNameConstruct(selectedReportItemInputMap)
        );

        setRIIMMode('add');
        handleRIIMReportItemInputMapResetForm('blank');
    };

    const getRIIMOperation = (): ManagedAdminChildOperation => {
        if (addButtonClicked) return 'add';
        if (updButtonClicked) return 'update';
        return 'delete';
    };

    const getRIIMActionColor = (): string => {
        if (addButtonClicked) return 'cornflowerblue';
        if (updButtonClicked) return 'darkgray';
        if (delButtonClicked) return 'red';
        return 'black';
    };

    const setRIIMBlankForm = () => {
        setRIIMReportInputID(0);
        setRIIMDataSetInputID(0);

        formAUDReportItemInputMap.setFieldValue('riimReportInputID', 0);
        formAUDReportItemInputMap.setFieldValue('riimDataSetInputID', 0);
    };

    const setRIIMFormFromSelectedReportItemInputMap = () => {
        const selectedReportItemInputMap = reportItemInputMaps.find(
            (reportItemInputMap) => reportItemInputMap.id === riimSelectedReportItemInputMapID
        );

        const selectedReportInputID = selectedReportItemInputMap?.reportInputID ?? 0;
        const selectedDataSetInputID = selectedReportItemInputMap?.dataSetInputID ?? 0;

        setRIIMReportInputID(selectedReportInputID);
        setRIIMDataSetInputID(selectedDataSetInputID);

        formAUDReportItemInputMap.setFieldValue('riimReportInputID', selectedReportInputID);
        formAUDReportItemInputMap.setFieldValue('riimDataSetInputID', selectedDataSetInputID);
    };

    const handleRIIMReportItemInputMapResetForm = (mode: ManagedAdminEditorMode) => {
        if (mode === 'blank') {
            setRIIMBlankForm();
            return;
        }

        setRIIMFormFromSelectedReportItemInputMap();
    };

    //=================================================================
    //=================================================================
    
    const handleRIIMAddReportItemInputMap = () => {
        clearRIIMSelection();
    };

    //=================================================================
    //=================================================================

    const handleRIIMUpdReportItemInputMap = () => {
        setRIIMMode('update');
        handleRIIMReportItemInputMapResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleRIIMDelReportItemInputMap = () => {
        setRIIMMode('delete');
        handleRIIMReportItemInputMapResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const onRIIMCancel = () => {
        clearRIIMSelection();
    };

    const handleCloseManagedActionModal = () => {
        setManagedActionModalState((currentState) => ({
            ...currentState,
            open: false,
        }));
    };

    const onFinishFailed = (errorInfo: ManagedFormErrorInfo<AUDReportItemInputMapFormValues>) => {
        console.error('AUDReportItemInputMaps validation failed:', errorInfo);

        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'Report Item Input Map Validation',
                'Please correct the highlighted Report Item Input Map fields and try again.',
                true
            )
        );
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10px',
        border: `1px solid black`,
        marginTop: '16px',
        paddingRight: '8px',
        paddingLeft: '8px',
        height: '375px'
    };

    return (
        <>
            <ManagedActionModal
                modalState={managedActionModalState}
                onOk={handleCloseManagedActionModal}
            />
            <Spin spinning={isGetReportInputsLoading || isGetDataSetInputsLoading}>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '4px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage Report Item Input Maps</Title>
                    </Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'Report: [' + formatRIIMDisplayID(riimPropsReportID) + '] ' + riimPropsReportGroupName + '-' + riimPropsReportName}</Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'Report Page: [' + formatRIIMDisplayID(riimPropsReportPageID) + '] ' + riimPropsReportPageTitle}</Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'Report Item: [' + formatRIIMDisplayID(riimPropsReportItemID) + '] ' + riimPropsReportItemTitle}</Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'Report Item Input Map: [' + formatRIIMDisplayID(riimSelectedReportItemInputMapID) + '] ' + riimSelectedReportItemInputMapTitle}</Text>
                </Space>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Space direction='horizontal' style={{width: '100%', justifyContent: 'left'}}>
                                <Text italic>Select an action below to proceed.</Text>
                            </Space>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '4px'}}>
                    <Col span={2} />
                    <Col span={8}>
                        <Text italic style={{float: 'right'}}>Select Report Item Input Map to Update or Delete:</Text>
                    </Col>
                    <Col span={14}>
                        <Select
                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            placeholder={'Select a Report Item Input Map'}
                            showSearch
                            styles={{ popup: { root: { width: 500 } } }}
                            style={{paddingTop: '4px', width: '95%'}}
                            value={riimSelectedReportItemInputMapID === -1 ? undefined : riimSelectedReportItemInputMapID}
                            onChange={handleRIIMSelectionChange}
                        >
                            {reportItemInputMaps.map(
                                (eachReportItemInputMap) => {
                                    return (
                                        <Option
                                            key={eachReportItemInputMap.id}
                                            label={buildReportItemInputMapNameConstruct(eachReportItemInputMap)}
                                            value={eachReportItemInputMap.id}
                                        >
                                            {buildReportItemInputMapNameConstruct(eachReportItemInputMap)}
                                        </Option>
                                    );
                                }
                            )}
                        </Select>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={6}>
                        <Button
                            icon={<AddIcon />}
                            style={{ float: 'right' , marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleRIIMAddReportItemInputMap}
                        >
                            Add Report Item Input Map
                        </Button>
                    </Col>
                    <Col span={5} />
                    <Col span={6}>
                        <Button
                            disabled={riimSelectedReportItemInputMapID === -1}
                            icon={<EditIcon />}
                            style={{ backgroundColor: 'darkgray', float: 'left', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleRIIMUpdReportItemInputMap}
                        >
                            Update Report Item Input Map
                        </Button>
                    </Col>
                    <Col span={1} />
                    <Col span={6}>
                        <Button
                            disabled={riimSelectedReportItemInputMapID === -1}
                            icon={<DeleteIcon />}
                            style={{ backgroundColor: 'red', float: 'left', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleRIIMDelReportItemInputMap}
                        >
                            Delete Report Item Input Map
                        </Button>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                </Row>
                <Form
                    form={formAUDReportItemInputMap}
                    initialValues={initialRIIMFormValues}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <div style={contentStyle}>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={21}>
                                <Divider orientation='left' orientationMargin={30} variant='dotted' style={{ borderColor: 'black', color: getRIIMActionColor() }}>
                                    {addButtonClicked ? 'Add Report Item Input Map' :
                                        (updButtonClicked ? 'Update Report Item Input Map: [' + formatRIIMDisplayID(riimSelectedReportItemInputMapID) + '] ' :
                                            (delButtonClicked ? 'Delete Report Item Input Map: [' + formatRIIMDisplayID(riimSelectedReportItemInputMapID) + '] ' : ''))}
                                </Divider>
                            </Col>
                            <Col span={3}>
                                <Button
                                    disabled={delButtonClicked}
                                    style={{ color: getRIIMActionColor(), fontStyle: 'italic' }}
                                    type='link'
                                    onClick={() => handleRIIMReportItemInputMapResetForm(addButtonClicked ? 'blank' : 'selected')}
                                >
                                    Reset Form
                                </Button>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={12}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='riimReportInputID'
                                        rules={[
                                            {
                                                message: 'Report Input ID required.',
                                                required: true,
                                                validator: validateRIIMDetailsData,
                                            },
                                        ]}
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                            label='Report Input ID:'
                                            optionFilterProp='label'
                                            showSearch
                                            value={riimReportInputID}
                                            onChange={handleRIIMReportInputIDChange}
                                        >
                                            <Option
                                                value={0}
                                                label={'(Not Set)'}
                                            >
                                                {'(Not Set)'}
                                            </Option>
                                            {reportInputs.map(
                                                (eachReportInput) => {
                                                    return (
                                                        <Option
                                                            key={eachReportInput.id}
                                                            label={eachReportInput.title}
                                                            value={eachReportInput.id}
                                                        >
                                                            {eachReportInput.title}
                                                        </Option>
                                                    );
                                                }
                                            )}
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={12}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='riimDataSetInputID'
                                        rules={[
                                            {
                                                message: 'DataSet Input ID required.',
                                                required: true,
                                                validator: validateRIIMDetailsData,
                                            },
                                        ]}
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                            label='DataSet Input ID:'
                                            optionFilterProp='label'
                                            showSearch
                                            value={riimDataSetInputID}
                                            onChange={handleRIIMDataSetInputIDChange}
                                        >
                                            <Option
                                                value={0}
                                                label={'(Not Set)'}
                                            >
                                                {'(Not Set)'}
                                            </Option>
                                            {dataSetInputs.map(
                                                (eachDataSetInput) => {
                                                    return (
                                                        <Option
                                                            key={eachDataSetInput.id}
                                                            label={eachDataSetInput.title}
                                                            value={eachDataSetInput.id}
                                                        >
                                                            {eachDataSetInput.title}
                                                        </Option>
                                                    );
                                                }
                                            )}
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                        </Row>
                    </div>
                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                        <Col span={6}>
                            <Form.Item
                                name='riimCloseButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={close}
                                >
                                    Close
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={6} />
                        <Col span={6}>
                            <Form.Item
                                name='riimApplyButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    htmlType='submit'
                                    style={{ backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')), float: 'right', width: '95%' }}
                                    type='primary'
                                >
                                    {addButtonClicked ? 'Add Report Item Input Map' : (updButtonClicked ? 'Update Report Item Input Map' : (delButtonClicked ? 'Delete Report Item Input Map' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name='riimCancelButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ float: 'right', width: '95%' }}
                                    type='default'
                                    onClick={onRIIMCancel}
                                >
                                    {addButtonClicked ? 'Cancel Add' : (updButtonClicked ? 'Cancel Update' : (delButtonClicked ? 'Cancel Delete' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </>
    );
};

export default AUDReportItemInputMaps;
